import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { 
  Terminal, 
  Play, 
  Award, 
  Users, 
  BookOpen, 
  Server, 
  Cloud,
  ArrowRight,
  CheckCircle,
  Zap,
  Code2
} from "lucide-react";

const Landing = () => {
  const features = [
    {
      icon: <Play className="w-6 h-6" />,
      title: "Vidéos HD",
      description: "Cours vidéo haute qualité avec sous-titres et transcriptions."
    },
    {
      icon: <Terminal className="w-6 h-6" />,
      title: "Labs Interactifs",
      description: "Pratiquez dans des environnements Docker, Kubernetes et Cloud réels."
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Certificats",
      description: "Obtenez des certificats reconnus à la fin de chaque cours."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Communauté",
      description: "Rejoignez une communauté de +10,000 professionnels DevOps."
    },
  ];

  const technologies = [
    { name: "Docker", icon: "🐳", color: "bg-blue-500/20 text-blue-400" },
    { name: "Kubernetes", icon: "☸️", color: "bg-purple-500/20 text-purple-400" },
    { name: "AWS", icon: "☁️", color: "bg-orange-500/20 text-orange-400" },
    { name: "Terraform", icon: "🏗️", color: "bg-violet-500/20 text-violet-400" },
    { name: "Jenkins", icon: "🔧", color: "bg-red-500/20 text-red-400" },
    { name: "Ansible", icon: "🔄", color: "bg-amber-500/20 text-amber-400" },
  ];

  const stats = [
    { value: "50+", label: "Cours" },
    { value: "10K+", label: "Étudiants" },
    { value: "500+", label: "Labs" },
    { value: "4.8", label: "Note moyenne" },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Grid */}
        <div className="absolute inset-0 grid-pattern opacity-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
                <Zap className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Nouvelle plateforme 2024</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-none mb-6">
                Maîtrisez le{" "}
                <span className="gradient-text">Cloud</span>{" "}
                et{" "}
                <span className="gradient-text">DevOps</span>
                <span className="inline-block w-3 h-8 bg-primary ml-1 cursor-blink"></span>
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-lg leading-relaxed">
                Apprenez Docker, Kubernetes, AWS, Terraform et plus encore avec des 
                <span className="text-foreground font-medium"> vidéos interactives</span> et des 
                <span className="text-foreground font-medium"> laboratoires pratiques</span>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/courses">
                  <Button size="lg" className="w-full sm:w-auto glow-green btn-glow" data-testid="hero-cta-courses">
                    Explorer les cours
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/auth?register=true">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto" data-testid="hero-cta-register">
                    Commencer gratuitement
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-heading font-bold text-primary">{stat.value}</div>
                    <div className="text-xs text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Terminal Preview */}
            <div className="relative animate-slide-up stagger-2">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg blur-xl opacity-50"></div>
              <div className="relative terminal rounded-lg overflow-hidden border border-border">
                <div className="bg-[#1a1a1a] px-4 py-2 flex items-center gap-2 border-b border-[#333]">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#27ca3f]"></div>
                  </div>
                  <span className="text-xs text-gray-400 ml-2 font-mono">devops@learn:~</span>
                </div>
                <div className="p-4 font-mono text-sm" style={{ background: "#0a0a0a" }}>
                  <div className="text-green-400 mb-2">$ kubectl get pods</div>
                  <div className="text-gray-300 mb-1">NAME                    READY   STATUS    RESTARTS   AGE</div>
                  <div className="text-cyan-400 mb-1">nginx-deployment-abc    1/1     Running   0          5d</div>
                  <div className="text-cyan-400 mb-3">api-server-xyz          1/1     Running   0          3d</div>
                  <div className="text-green-400 mb-2">$ docker ps</div>
                  <div className="text-gray-300 mb-1">CONTAINER ID   IMAGE    STATUS   PORTS</div>
                  <div className="text-cyan-400 mb-1">a1b2c3d4e5f6   nginx    Up 2h    80/tcp</div>
                  <div className="flex items-center text-green-400">
                    <span>$</span>
                    <span className="w-2 h-4 bg-green-400 ml-1 cursor-blink"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 border-y border-border/40 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-sm font-heading uppercase tracking-widest text-muted-foreground mb-4">
              Technologies couvertes
            </h2>
            <p className="text-2xl font-heading font-bold">
              Apprenez les outils les plus demandés
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, index) => (
              <div 
                key={index}
                className={`${tech.color} px-6 py-3 rounded-full font-medium flex items-center gap-2 border border-current/20`}
              >
                <span className="text-xl">{tech.icon}</span>
                <span>{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-sm font-heading uppercase tracking-widest text-primary mb-4">
              Pourquoi DevOpsLearn
            </h2>
            <p className="text-3xl sm:text-4xl font-heading font-bold">
              Une approche{" "}
              <span className="gradient-text">pratique</span>{" "}
              de l'apprentissage
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-sm p-6 card-hover"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-card/50 border-y border-border/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-secondary/10 border border-secondary/20 rounded-full px-4 py-1.5 mb-6">
            <Cloud className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium text-secondary">Prêt à commencer?</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
            Rejoignez +10,000 professionnels qui font évoluer leur carrière
          </h2>
          
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Accédez à tous nos cours, labs et certifications avec notre abonnement Pro.
            Satisfait ou remboursé pendant 30 jours.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button size="lg" className="glow-green">
                Voir les tarifs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Annulation à tout moment
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Support 24/7
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
